/**
 * WatchPlanDialog — the published mkpaceLIVE URL for a race plan.
 *
 * A published URL is an immutable pointer to one plan at one granularity. The
 * watch fetches it once at the start line and caches it; the only thing that
 * makes it drop that cache is the URL in its Connect IQ settings changing
 * (onSettingsChanged → PlanStore.clear). So this dialog is READ-ONLY by default —
 * simply opening it must never invalidate a URL already on someone's watch — and
 * pushing a changed plan is a deliberate, confirmed "publish new link" that
 * revokes the old token and mints a new one for the user to re-paste.
 */

import { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Alert, Chip, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import WatchIcon from '@mui/icons-material/Watch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from 'react-i18next';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ResponsiveContainer } from 'recharts';
import { getWatchPlanCheckpoints } from '../../utils/racePlanBoundaries';
import { revokeWatchPlan } from '../../api/runningApi';

/**
 * The mkpaceLIVE user manual — a self-contained HTML build copied from the watch
 * project (its docs/manual/manual.html), served statically from /public/help.
 * nginx's `try_files $uri $uri/ /index.html` lets the real file win over the
 * /help/:slug SPA route, so this doubles as a stable public URL to hand out in the
 * Connect IQ store listing and other watch materials.
 */
export const MKPACELIVE_MANUAL_URL =
  (process.env.PUBLIC_URL || '') + '/help/mkpacelive-manual.html';

// Short, task-focused companion to the full manual. Served statically at the same
// /help path, so it also has a stable public URL to hand out on its own.
export const MKPACELIVE_QUICKGUIDE_URL =
  (process.env.PUBLIC_URL || '') + '/help/mkpacelive-quickguide.html';

export default function WatchPlanDialog({
  open,
  onClose,
  link,
  error,
  building,           // waiting on the race plan build, not on the publish
  granularity,        // what the Plan toggle shows right now
  chunks = [],        // for the elevation preview
  segments = [],      // DETAILED plan segments; the preview derives its own view
  onRepublish,        // async (granularity) => newLink
  onUnpublished,
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmingReplace, setConfirmingReplace] = useState(false);

  const url = link?.url || '';
  const published = link?.granularity;
  const isMinimal = published === 'minimal';
  // The published link serves what it was published with, NOT what the toggle
  // currently shows — say so plainly rather than letting the user assume.
  const mismatch = Boolean(published && granularity && published !== granularity);

  const label = (g) => (g === 'minimal' ? t('planMinimal', 'Minimal') : t('planDetailed', 'Detailed'));

  // Preview the PUBLISHED plan, not whatever the toggle shows now. The URL keeps
  // serving what it was minted with, so previewing the current toggle would show
  // a plan the watch will never receive.
  const watchCps = useMemo(
    () => getWatchPlanCheckpoints(segments, published || granularity),
    [segments, published, granularity],
  );

  // Which checkpoints get their name drawn.
  //
  // Names are staggered across two heights, so neighbours only a few km apart
  // sit clear of each other instead of one being dropped. A first attempt kept
  // just one name per ninth of the course, which silently swallowed a real aid
  // station 5 km after its neighbour and left an anonymous dotted line — the
  // one thing this preview must not do, since naming the checkpoints is the
  // whole point of it.
  //
  // Thinning only kicks in past what two rows can hold; a detailed plan on a
  // long race has dozens of boundaries and no arrangement fits them all.
  const labelRows = useMemo(() => {
    const rows = new Map();
    if (!watchCps.length) return rows;
    const total = watchCps[watchCps.length - 1].distance_m || 1;
    const capacity = 14;                       // comfortable across two rows
    const minGap = watchCps.length > capacity ? total / capacity : 0;
    let last = -Infinity;
    let i = 0;
    for (const cp of watchCps) {
      if (cp.distance_m - last < minGap) continue;
      rows.set(cp.distance_m, i % 2);          // 0 = lower row, 1 = upper
      last = cp.distance_m;
      i += 1;
    }
    return rows;
  }, [watchCps]);

  const profile = useMemo(() => {
    let cum = 0;
    return (chunks || []).map((c) => {
      cum += c.distance || 0;
      return { km: cum / 1000, altitude: c.avg_altitude ?? null };
    });
  }, [chunks]);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError(t('watchPlanCopyFailed', 'Could not copy — select the URL and copy it manually.'));
    }
  };

  const handleReplace = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await onRepublish?.(granularity);
      setConfirmingReplace(false);
    } catch {
      setActionError(t('watchPlanReplaceFailed', 'Could not publish a new link. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const handleUnpublish = async () => {
    if (!link?.token) return;
    setBusy(true);
    setActionError(null);
    try {
      await revokeWatchPlan(link.token);
      onUnpublished?.();
      onClose?.();
    } catch {
      setActionError(t('watchPlanUnpublishFailed', 'Could not unpublish. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setConfirmingReplace(false);
    setActionError(null);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WatchIcon fontSize="small" />
        {t('watchPlanTitle', 'mkpaceLIVE watch plan')}
      </DialogTitle>

      <DialogContent>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : building ? (
          /* The plan has to exist before it can be published, and building it names
             every climb, descent and landmark on the route from OpenStreetMap. The
             first file from an unfamiliar area pays for those lookups, which is slow
             enough that an unlabelled spinner looks like a hang — so say so. */
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} py={3} px={2}>
            <CircularProgress size={28} />
            <Typography variant="body1">
              {t('watchPlanBuildingTitle', 'Building your race plan…')}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {t(
                'watchPlanBuildingDesc',
                'We are naming the climbs, descents and landmarks along your route. For a file from an area we have not seen before this can take up to a minute. It is saved afterwards, so the next time is instant.',
              )}
            </Typography>
          </Box>
        ) : !link ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                {t('watchPlanServing', 'This URL serves the')}
              </Typography>
              <Chip size="small" color={isMinimal ? 'default' : 'primary'} label={label(published)} />
              <Typography variant="body2" color="text.secondary">
                {t('watchPlanServingSuffix', 'plan.')}
              </Typography>
            </Box>

            {/* What the watch will actually count down to. Worth showing: the two
                granularities ship different checkpoints, and until now the only
                way to know which you had published was to read the chip. */}
            {profile.length > 1 && (
              <Box mb={2}>
                <Box sx={{ height: 225 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {/* Generous top margin: a ReferenceLine label is drawn ABOVE
                        the plot area, so anything less clips the names away. */}
                    <LineChart data={profile} margin={{ top: 78, right: 16, bottom: 2, left: 0 }}>
                      <XAxis dataKey="km" type="number" domain={['dataMin', 'dataMax']}
                        tickFormatter={(v) => v.toFixed(0)} fontSize={10} />
                      <YAxis fontSize={10} width={40}
                        tickFormatter={(v) => (v == null ? '' : Math.round(v))} />
                      <Line type="monotone" dataKey="altitude" dot={false}
                        stroke="#42a5f5" strokeWidth={1.4} isAnimationActive={false} />
                      {watchCps.map((cp, i) => (
                        <ReferenceLine
                          key={i}
                          x={cp.distance_m / 1000}
                          stroke={cp.kind === 'manual' ? '#ab47bc'
                            : cp.kind === 'checkpoint' ? '#26a69a' : '#90a4ae'}
                          strokeDasharray="3 3"
                          strokeWidth={1.2}
                          label={labelRows.has(cp.distance_m) ? {
                            value: cp.label,
                            position: 'top',
                            angle: -40,
                            fontSize: 9,
                            // Alternating heights keep close neighbours legible.
                            offset: labelRows.get(cp.distance_m) ? 30 : 6,
                            fill: cp.kind === 'manual' ? '#ab47bc'
                              : cp.kind === 'checkpoint' ? '#26a69a' : '#90a4ae',
                          } : undefined}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('watchPlanCpCount',
                     '{{n}} checkpoints on the watch, shown dotted.', { n: watchCps.length })}
                  {isMinimal
                    ? ' ' + t('watchPlanMinimalNote',
                        'Minimal sends aid stations and your own checkpoints only.')
                    : ' ' + t('watchPlanDetailedNote',
                        'Detailed also sends every climb and descent boundary.')}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1, mb: 2,
              }}
            >
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-all', flex: 1 }}>
                {url}
              </Typography>
              <Tooltip title={copied ? t('copied', 'Copied') : t('copy', 'Copy')}>
                <IconButton size="small" onClick={handleCopy}>
                  {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>

            {mismatch && !confirmingReplace && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('watchPlanMismatch', 'You are viewing the {{now}} plan, but this URL was published from the {{was}} one. Publish a new link to send the {{now}} plan to your watch.', {
                  now: label(granularity), was: label(published),
                })}
              </Alert>
            )}

            {confirmingReplace ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{t('watchPlanReplaceConfirmTitle', 'Publish a new link?')}</strong>
                </Typography>
                <Typography variant="body2">
                  {t('watchPlanReplaceConfirmBody', 'The URL above stops working immediately and a new one takes its place, serving the {{now}} plan. You must paste the new URL into the mkpaceLIVE settings in Garmin Connect — that is what makes your watch drop the plan it already downloaded and fetch this one.', { now: label(granularity) })}
                </Typography>
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  {t('watchPlanInstallTitle', 'Installing on your watch')}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <ol style={{ margin: 0, paddingLeft: 18 }}>
                    <li>{t('watchPlanStep1', 'Install mkpaceLIVE on your watch from the Connect IQ store and add it as a data field on a run screen.')}</li>
                    <li>
                      {t('watchPlanStep2pre', 'In the Garmin Connect app, open')}{' '}
                      <strong>{t('watchPlanStep2path', 'Connect IQ → mkpaceLIVE → Settings')}</strong>
                      {t('watchPlanStep2post', ' and paste this URL into "Plan URL".')}
                    </li>
                    <li>{t('watchPlanStep3', 'Start your run at the start line, where you still have phone signal — the field downloads the plan within about 15 seconds.')}</li>
                    <li>{t('watchPlanStep4', 'It is then saved on the watch and works fully offline for the whole race.')}</li>
                  </ol>
                </Typography>

                <Button
                  component="a"
                  href={MKPACELIVE_MANUAL_URL}
                  target="_blank"
                  rel="noopener"
                  size="small"
                  startIcon={<MenuBookIcon />}
                  sx={{ mt: 1.5, px: 0, textTransform: 'none' }}
                >
                  {t('watchPlanManual', 'Open the mkpaceLIVE user manual')}
                </Button>
                <Button
                  component="a"
                  href={MKPACELIVE_QUICKGUIDE_URL}
                  target="_blank"
                  rel="noopener"
                  size="small"
                  startIcon={<MenuBookIcon />}
                  sx={{ mt: 0.5, px: 0, textTransform: 'none', display: 'block' }}
                >
                  {t('watchPlanQuickGuide', 'Open the mkpaceLIVE quick guide')}
                </Button>
              </>
            )}

            {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {link && !confirmingReplace && (
          <Button onClick={handleUnpublish} disabled={busy} color="error" sx={{ mr: 'auto' }}>
            {t('watchPlanUnpublish', 'Stop publishing')}
          </Button>
        )}
        {link && !confirmingReplace && (
          <Button
            onClick={() => setConfirmingReplace(true)}
            disabled={busy}
            startIcon={<AutorenewIcon />}
            color={mismatch ? 'warning' : 'inherit'}
          >
            {t('watchPlanReplace', 'Publish new link')}
          </Button>
        )}
        {confirmingReplace && (
          <>
            <Button onClick={() => setConfirmingReplace(false)} disabled={busy} sx={{ mr: 'auto' }}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleReplace}
              disabled={busy}
              color="warning"
              variant="contained"
              startIcon={busy ? <CircularProgress size={14} /> : <AutorenewIcon />}
            >
              {t('watchPlanReplaceConfirm', 'Replace link')}
            </Button>
          </>
        )}
        <Button onClick={handleClose}>{t('close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
